import _ from 'lodash';
import moment from 'moment';
import { timeAndPrecisionTypes } from 'smartsuite-ui';
import { precisionTypes } from '../constants/constants';

/* eslint-disable complexity */
function calculateDifferenceBetweenTimeAndRangeValue(
    currentTime,
    { timeAndPrecisionType, rangeType, rangeValue }
) {
    if (!Object.keys(precisionTypes).includes(rangeType)) {
        throw Error(`${rangeType} not supported`);
    }

    const multiplier =
        timeAndPrecisionType === timeAndPrecisionTypes.next ? 1 : -1;

    return moment(currentTime)
        .add(multiplier * rangeValue, rangeType)
        .startOf('minute');
}

function calculatePredefinedStartTime(
    currentTime,
    { predefinedType, predefinedValue }
) {
    if (!Object.keys(precisionTypes).includes(predefinedType)) {
        throw Error(`${predefinedType} not supported`);
    }

    return currentTime
        .add(predefinedValue, predefinedType)
        .startOf(predefinedType);
}

function roundDownDateTime(dateTime, precisionType, precisionTypeValue) {
    if (!Object.keys(precisionTypes).includes(precisionType)) {
        throw Error(`${precisionType} not supported`);
    }

    switch (precisionType) {
        case precisionTypes.minute: {
            return moment(dateTime)
                .add(-(dateTime.minute() % precisionTypeValue), precisionType)
                .startOf(precisionType);
        }
        default: {
            return moment(dateTime).startOf(precisionType);
        }
    }
}

function calculateStartTime(currentTime, timeAndPrecision, isGauge) {
    switch (timeAndPrecision.timeAndPrecisionType) {
        case timeAndPrecisionTypes.fixed: {
            return moment(timeAndPrecision.startDateTime);
        }
        case timeAndPrecisionTypes.fixedDay: {
            return moment(timeAndPrecision.startDateTime).startOf('day');
        }
        case timeAndPrecisionTypes.last: {
            const startTime = calculateDifferenceBetweenTimeAndRangeValue(
                currentTime,
                timeAndPrecision
            );

            // prettier-ignore
            return !isGauge
                ? startTime
                : roundDownDateTime(
                    startTime,
                    timeAndPrecision.precisionType,
                    timeAndPrecision.precisionValue
                );
        }
        case timeAndPrecisionTypes.next:
            return currentTime.startOf('minute');
        case timeAndPrecisionTypes.predefined: {
            return calculatePredefinedStartTime(
                currentTime,
                timeAndPrecision.selectedDateRangeOption
            );
        }
        default: {
            return moment(timeAndPrecision.startDateTime);
        }
    }
}

function calculatePredefinedEndTime(
    { predefinedType, predefinedLength },
    startTime
) {
    if (!Object.keys(precisionTypes).includes(predefinedType)) {
        throw Error(`${predefinedType} not supported`);
    }

    return moment(startTime)
        .add(predefinedLength, predefinedType)
        .startOf(predefinedType);
}

function calculateEndTime(currentTime, timeAndPrecision, startTime) {
    switch (timeAndPrecision.timeAndPrecisionType) {
        case timeAndPrecisionTypes.fixed: {
            return moment(timeAndPrecision.endDateTime);
        }
        case timeAndPrecisionTypes.fixedDay: {
            return moment(timeAndPrecision.startDateTime)
                .startOf('day')
                .add(1, 'days');
        }
        case timeAndPrecisionTypes.last: {
            return currentTime.startOf('minute');
        }
        case timeAndPrecisionTypes.next: {
            return calculateDifferenceBetweenTimeAndRangeValue(
                currentTime,
                timeAndPrecision
            );
        }
        case timeAndPrecisionTypes.predefined: {
            return calculatePredefinedEndTime(
                timeAndPrecision.selectedDateRangeOption,
                startTime
            );
        }
        default: {
            return moment(timeAndPrecision.endDateTime);
        }
    }
}
/* eslint-enable complexity */

const MINUTES_IN_HOUR = 60;
const MINUTES_IN_DAY = MINUTES_IN_HOUR * 24;
const MINUTES_IN_WEEK = MINUTES_IN_DAY * 7;
const MINUTES_IN_YEAR = MINUTES_IN_DAY * 365;
const timeAndPrecisionRules = [
    {
        precisionType: 'minute',
        breakdownMin: 1,
        breakdownMax: 59,
        maxRangeValue: 1,
        maxRangeType: 'day',
        maxRangeInMinutes: MINUTES_IN_DAY,
    },
    {
        precisionType: 'hour',
        breakdownMin: 1,
        breakdownMax: 23,
        maxRangeValue: 1,
        maxRangeType: 'week',
        maxRangeInMinutes: MINUTES_IN_WEEK,
    },
    {
        precisionType: 'day',
        breakdownMin: 1,
        breakdownMax: 28,
        maxRangeValue: 1,
        maxRangeType: 'year',
        maxRangeInMinutes: MINUTES_IN_YEAR,
    },
    {
        precisionType: 'week',
        breakdownMin: 1,
        breakdownMax: 50,
        maxRangeValue: 2,
        maxRangeType: 'year',
        maxRangeInMinutes: MINUTES_IN_YEAR * 2,
    },
    {
        precisionType: 'month',
        breakdownMin: 1,
        breakdownMax: 11,
        maxRangeValue: 5,
        maxRangeType: 'year',
        maxRangeInMinutes: MINUTES_IN_YEAR * 5,
    },
    {
        precisionType: 'quarter',
        breakdownMin: 1,
        breakdownMax: 3,
        maxRangeValue: 5,
        maxRangeType: 'year',
        maxRangeInMinutes: MINUTES_IN_YEAR * 5,
    },
    {
        precisionType: 'year',
        breakdownMin: 1,
        breakdownMax: 3,
        maxRangeValue: 5,
        maxRangeType: 'year',
        maxRangeInMinutes: MINUTES_IN_YEAR * 5,
    },
];

function isRuleRelevant(timeAndPrecision, rule) {
    return timeAndPrecision.precisionType === rule.precisionType;
}

function isBreakdownValid(timeAndPrecision, rule) {
    return (
        timeAndPrecision.precisionValue >= rule.breakdownMin &&
        timeAndPrecision.precisionValue <= rule.breakdownMax
    );
}

function isMaxRangeExceeded(startDateTime, endDateTime, rule) {
    const differenceInMinutes = endDateTime.diff(startDateTime, 'minute', true);
    return differenceInMinutes > rule.maxRangeInMinutes;
}

function isBreakdownLongerThanRange(
    currentTime,
    timeAndPrecision,
    startDateTime,
    endDateTime
) {
    const precisionEnd = moment(currentTime);

    const precisionStart = moment(currentTime).add(
        timeAndPrecision.precisionValue,
        timeAndPrecision.precisionType
    );
    const precisionInMinutes = precisionStart.diff(
        precisionEnd,
        'minute',
        true
    );
    const rangeInMinutes = endDateTime.diff(startDateTime, 'minute', true);

    return precisionInMinutes > rangeInMinutes;
}

export function isPrecisionValidForRange(timeAndPrecision) {
    if (
        !timeAndPrecision.rangeValue &&
        (timeAndPrecision.timeAndPrecisionType === timeAndPrecisionTypes.last ||
            timeAndPrecision.timeAndPrecisionType ===
                timeAndPrecisionTypes.next)
    ) {
        return true;
    }

    const currentTime = moment();

    const startDateTime = calculateStartTime(
        currentTime,
        timeAndPrecision,
        false
    );
    const endDateTime = calculateEndTime(
        currentTime,
        timeAndPrecision,
        startDateTime
    );
    const rule = timeAndPrecisionRules.find(
        (x) => x.precisionType === timeAndPrecision.precisionType
    );

    const breakdownValid = isBreakdownValid(timeAndPrecision, rule);
    const maxRangeExceeded = isMaxRangeExceeded(
        startDateTime,
        endDateTime,
        rule
    );
    const breakdownLongerThanRange = isBreakdownLongerThanRange(
        currentTime,
        timeAndPrecision,
        startDateTime,
        endDateTime
    );

    return breakdownValid && !maxRangeExceeded && !breakdownLongerThanRange;
}

// eslint-disable-next-line complexity
export function getSmallestPrecisionTypeAndPrecisionValue(
    timeAndPrecision,
    precisionTypeOptions
) {
    const currentTime = moment();

    const precisionKeys = Object.keys(precisionTypes);
    const startDateTime = calculateStartTime(
        currentTime,
        timeAndPrecision,
        false
    );
    const endDateTime = calculateEndTime(
        currentTime,
        timeAndPrecision,
        startDateTime
    );

    const filteredPrecisionTypes =
        precisionTypeOptions?.[timeAndPrecision.rangeType] ?? precisionKeys;

    let precisionType =
        filteredPrecisionTypes[filteredPrecisionTypes.length - 1];

    filteredPrecisionTypes.every((precisionKey) => {
        const rule = timeAndPrecisionRules.find(
            (x) => x.precisionType === precisionKey
        );

        if (!isMaxRangeExceeded(startDateTime, endDateTime, rule)) {
            precisionType = precisionKey;
            return false;
        }

        return true;
    });

    const precisionValue = !isBreakdownLongerThanRange(
        currentTime,
        { ...timeAndPrecision, precisionType },
        startDateTime,
        endDateTime
    )
        ? timeAndPrecision.precisionValue ?? 1
        : 1;

    return { precisionType, precisionValue };
}

function getTranslatedRule(rule, dashboardLabels) {
    return _.mapValues(rule, (value) => dashboardLabels[value] ?? value);
}

export function validateTimeRange(
    timeRange,
    interpolateString,
    dashboardLabels,
    isGauge = false
) {
    if (isGauge) {
        return { isTimeAndPrecisionValid: true };
    }

    const currentTime = moment();

    const startDateTime = calculateStartTime(currentTime, timeRange, false);
    const endDateTime = calculateEndTime(currentTime, timeRange, startDateTime);

    const errors = [];

    /* eslint-disable complexity */
    const isTimeRangeValid = timeAndPrecisionRules.every((rule) => {
        let rulePassed = true;
        const translatedRule = getTranslatedRule(rule, dashboardLabels);

        if (!isRuleRelevant(timeRange, rule)) {
            return rulePassed;
        }

        if (isMaxRangeExceeded(startDateTime, endDateTime, rule)) {
            errors.push(
                interpolateString(
                    'dashboardLabels:maxRangeExceeded',
                    translatedRule
                )
            );
            rulePassed = false;
        }

        return rulePassed;
    });
    /* eslint-enable complexity */

    return { isTimeRangeValid, errors };
}

export function validateTimeAndPrecision(
    timeAndPrecision,
    interpolateString,
    dashboardLabels,
    isGauge = false
) {
    if (isGauge) {
        return { isTimeAndPrecisionValid: true };
    }

    const currentTime = moment();

    const startDateTime = calculateStartTime(
        currentTime,
        timeAndPrecision,
        false
    );
    const endDateTime = calculateEndTime(
        currentTime,
        timeAndPrecision,
        startDateTime
    );

    const errors = [];

    /* eslint-disable complexity */
    const isTimeAndPrecisionValid = timeAndPrecisionRules.every((rule) => {
        let rulePassed = true;
        const translatedRule = getTranslatedRule(rule, dashboardLabels);

        if (!isRuleRelevant(timeAndPrecision, rule)) {
            return rulePassed;
        }

        if (!isBreakdownValid(timeAndPrecision, rule)) {
            errors.push(
                interpolateString(
                    'dashboardLabels:invalidBreakdown',
                    translatedRule
                )
            );
            rulePassed = false;
        }

        if (isMaxRangeExceeded(startDateTime, endDateTime, rule)) {
            errors.push(
                interpolateString(
                    'dashboardLabels:maxRangeExceeded',
                    translatedRule
                )
            );
            rulePassed = false;
        }

        if (
            isBreakdownLongerThanRange(
                currentTime,
                timeAndPrecision,
                startDateTime,
                endDateTime
            )
        ) {
            errors.push(
                interpolateString('dashboardLabels:breakdownExceedsRange')
            );
            rulePassed = false;
        }

        return rulePassed;
    });
    /* eslint-enable complexity */

    return { isTimeAndPrecisionValid, errors };
}
